// src/modulos/Desenvolvimento/DevelopmentPage.js
// VERSION: 3.4 - Fixed pagination and button refresh issues

import React, { useState, useMemo, useCallback, useEffect, useRef, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import useAuth from '../../hooks/useAuth';
import { exportBriefing } from '../../documents/exportBriefing';
import { FiClock } from 'react-icons/fi';
import styles from './Project.module.css';

// Notification imports
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';
import { doc, getDoc, getDocs, collection, addDoc, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

// Reusable Components
import {
    PageHeader,
    SearchBar,
    Pagination,
    TableSkeleton,
    Toast,
} from '../../components/common';

// React Icons
import {
    FiPlus,
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiPackage,
    FiClipboard,
    FiHome,
} from 'react-icons/fi';
import { FaMicroscope } from "react-icons/fa";
import { IoCheckmarkOutline } from "react-icons/io5";
import { SlChemistry } from "react-icons/sl";
import { GiSandsOfTime } from "react-icons/gi";
import { GrDocumentTransfer } from 'react-icons/gr';
import { GrDeliver } from "react-icons/gr";
import { VscChecklist } from "react-icons/vsc";

// ============================================
// 1. CONSTANTS AND DEFINITIONS
// ============================================
const PROJECTS_COLLECTION = 'development-projects';
const ITEMS_PER_PAGE = 10;
const LOADING_TIMEOUT = 8000;

const PROJECT_STEPS = [
    { id: 'formula', label: 'Sample in Formula Development', description: '', icon: <SlChemistry /> },
    { id: 'manipulation', label: 'Sample in Manipulation Process', description: 'Sample preparation and manipulation', icon: <FaMicroscope /> },
    { id: 'quality', label: 'Sample under Quality Control', description: 'Quality analysis and testing', icon: <IoCheckmarkOutline /> },
    { id: 'client', label: 'Sample awain', description: 'Sample shipment for client approval', icon: <GrDeliver /> },
    { id: 'status', label: 'Waiting for Client Approval', description: 'Sample approval or rejection', icon: <VscChecklist /> },
];

const PRODUCT_TYPE_OPTIONS = [
    'Shampoo', 'Conditioner', 'Hair Mask', 'Straightening', 'Leave-in',
    'Oil/Serum', 'Spray', 'Treatment kit', 'Others'
];

const TARGET_AUDIENCE_OPTIONS = ['Female', 'Male', 'Unisex', 'Baby (0-3)', 'Kids (3-12)', 'Adolescent'];
const PHYSICAL_FORM_OPTIONS = ['Liquid', 'Creamy', 'Oily', 'Powder'];
const VISCOSITY_OPTIONS = ['Fluid', 'Medium', 'Thick'];
const FRAGRANCE_OPTIONS = ['Floral', 'Woody', 'Herbal', 'Fresh', 'Sweet', 'Fruity', 'Citrus', 'Other'];
const COMPLIANCE_OPTIONS = ['ANVISA', 'FDA', 'CPNP', 'SFDA', 'Others'];
const KEY_CLAIMS_OPTIONS = ['Sulfate Free', 'Formaldehyde Free', 'Paraben Free', 'Cruelty Free', 'Vegan', 'Organic', 'Allergenic Free', 'Halal', 'Silicone Free', 'Other'];
const SITUATION_OPTIONS = ['Active', 'On Hold', 'Completed', 'Cancelled'];
const DEFAULT_SITUATION = SITUATION_OPTIONS[0];

const DEFAULT_PROJECT_MODEL = {
    code: '',
    projectName: '',
    clientId: '',
    clientName: '',
    clientContact: '',
    description: '',
    situation: DEFAULT_SITUATION,
    createdAt: null,
    updatedAt: null,
    createdBy: '',
    briefing: {
        startDate: new Date().toISOString().split('T')[0],
        brandName: '',
        productType: '',
        otherProductType: '',
        targetAudience: '',
        competitors: '',
        physicalForm: '',
        viscosity: '',
        fragrance: '',
        otherFragrance: '',
        requiredCompliance: '',
        otherCompliance: '',
        keyClaims: [],
        otherKeyClaims: '',
        productFunctions: [],
        sampleDeliveryDate: '',
        finalProductDeliveryDate: '',
        additionalInfo: [],
    },
};

// ============================================
// 2. AUXILIARY COMPONENTS
// ============================================

// Loading Overlay Component
const LoadingOverlay = ({ onNavigateHome }) => {
    const { t } = useTranslation();

    return (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
                <h3>{t('pages.development.loading')}</h3>
                <p>{t('pages.development.loadingMessage')}</p>
                <button
                    onClick={onNavigateHome}
                    className={styles.navigationButton}
                >
                    <FiHome size={18} />
                    {t('common.goToDashboard')}
                </button>
            </div>
        </div>
    );
};

// Status Badge Component
const StatusBadge = ({ status, t }) => {
    const getStatusStyle = () => {
        const activeLabel = t('pages.development.situation.active');
        const onHoldLabel = t('pages.development.situation.onHold');
        const completedLabel = t('pages.development.situation.completed');
        const cancelledLabel = t('pages.development.situation.cancelled');

        switch (status) {
            case activeLabel:
                return { background: '#d4edda', color: '#155724', borderLeft: '3px solid #28a745' };
            case onHoldLabel:
                return { background: '#fff3cd', color: '#856404', borderLeft: '3px solid #ffc107' };
            case completedLabel:
                return { background: '#d1ecf1', color: '#0c5460', borderLeft: '3px solid #17a2b8' };
            case cancelledLabel:
                return { background: '#f8d7da', color: '#721c24', borderLeft: '3px solid #dc3545' };
            default:
                return { background: '#e9ecef', color: '#6c757d', borderLeft: '3px solid #6c757d' };
        }
    };

    return (
        <span className={styles.statusBadge} style={getStatusStyle()}>
            {status}
        </span>
    );
};

// Project Step Status Component
const ProjectStepStatus = ({ project, t }) => {
    const getCurrentStep = () => {
        if (!project || !project.steps) return { id: 'pending', label: 'Not Started', icon: <GiSandsOfTime /> };

        const steps = project.steps;

        if (steps.status?.status === 'approved') {
            return { id: 'approved', label: 'Sample Approved by Client', icon: '✅' };
        }

        if (steps.status?.status === 'rejected') {
            return { id: 'rejected', label: 'Sample Rejected by Client', icon: '❌' };
        }

        if (steps.client?.status === 'completed' || steps.client?.status === 'sent') {
            if (!steps.status || steps.status.status === 'pending') {
                return {
                    id: 'waiting_approval',
                    label: 'Waiting for Client Approval',
                    icon: <FiClock />
                };
            }
        }

        if (steps.client?.status === 'in_progress' || steps.client?.status === 'pending') {
            return {
                id: 'sending',
                label: "Sample Sent to Client",
                icon: <GrDeliver />
            };
        }

        if (steps.quality?.status === 'completed') {
            if (!steps.client || steps.client.status === 'pending') {
                return {
                    id: 'ready_to_send',
                    label: 'Sample Ready to be Sent',
                    icon: <FiPackage />
                };
            }
        }

        const currentStep = PROJECT_STEPS.find(step => {
            const stepStatus = steps[step.id]?.status;
            return stepStatus !== 'completed' && stepStatus !== 'approved' && stepStatus !== 'sent';
        });

        if (currentStep) {
            const stepLabels = {
                'formula': 'Sample under Formula Development',
                'manipulation': 'Samnple in manipulation Process',
                'quality': 'Sample Under Quality Control',
                'client': 'Sample in Shipping Process',
                'status': 'Sample Waiting for client Approval'
            };
            return {
                ...currentStep,
                label: stepLabels[currentStep.id] || currentStep.label
            };
        }

        return { id: 'completed', label: 'All Steps were Finished', icon: '✓' };
    };

    const currentStep = getCurrentStep();

    const getStepStyle = () => {
        if (currentStep.id === 'pending') {
            return {
                background: '#e9ecef',
                color: '#6c757d',
                borderLeft: '3px solid #6c757d',
                padding: '4px 10px',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.85rem'
            };
        }

        if (currentStep.id === 'approved') {
            return {
                background: '#d4edda',
                color: '#155724',
                borderLeft: '3px solid #28a745',
                padding: '4px 10px',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.85rem'
            };
        }

        if (currentStep.id === 'rejected') {
            return {
                background: '#f8d7da',
                color: '#721c24',
                borderLeft: '3px solid #dc3545',
                padding: '4px 10px',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.85rem'
            };
        }

        if (currentStep.id === 'waiting_approval') {
            return {
                background: '#cce5ff',
                color: '#004085',
                borderLeft: '3px solid #007bff',
                padding: '4px 10px',
                borderRadius: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.85rem'
            };
        }

        return {
            background: '#fff3cd',
            color: '#856404',
            borderLeft: '3px solid #ffc107',
            padding: '4px 10px',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '0.85rem'
        };
    };

    return (
        <span style={getStepStyle()}>
            {currentStep.icon} {currentStep.label}
        </span>
    );
};

// Form Section Component
const FormSection = ({ title, icon, children, required = false }) => (
    <div className={styles.card}>
        <div className={styles.cardHeader}>
            <span className={styles.cardIcon}>{icon}</span>
            <h3 className={styles.cardTitle}>{title}</h3>
            {required && <span className={styles.sectionBadge}>Required</span>}
        </div>
        <div className={styles.cardBody}>
            {children}
        </div>
    </div>
);

// ============================================
// 3. MAIN COMPONENT
// ============================================
const DevelopmentPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const can = usePagePermissions('desenvolvimento');
    const { logActivity } = useActivityLogger();
    const { addNotification } = useNotifications();
    const currentUser = useAuth();

    // --- Transition for smooth UI updates ---
    const [isPending, startTransition] = useTransition();

    // --- Modal States ---
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');

    // --- Pagination and Filter States ---
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSituation, setFilterSituation] = useState('');

    // --- Loading timeout state ---
    const [loadingTimeout, setLoadingTimeout] = useState(false);
    const loadingTimeoutRef = useRef(null);

    // --- Form States - Section 1: Basic Information ---
    const [code, setCode] = useState('');
    const [projectName, setProjectName] = useState('');
    const [clientId, setClientId] = useState('');
    const [clientName, setClientName] = useState('');
    const [clientContact, setClientContact] = useState('');
    const [description, setDescription] = useState('');
    const [situation, setSituation] = useState(DEFAULT_SITUATION);

    // --- Form States - Section 2: Briefing ---
    const [briefing, setBriefing] = useState(DEFAULT_PROJECT_MODEL.briefing);

    // --- States for Dynamic Briefing Inputs ---
    const [functionInput, setFunctionInput] = useState('');
    const [additionalInfoInput, setAdditionalInfoInput] = useState('');
    const [otherKeyClaimsInput, setOtherKeyClaimsInput] = useState('');

    // --- Data Hooks ---
    const {
        data: projects,
        loading: mainLoading,
        addDocument,
        updateDocument,
        deleteDocument,
    } = useFirestore(PROJECTS_COLLECTION, 'code');

    const { data: clients, loading: clientsLoading } = useFirestore('clients', 'razaoSocial');

    // --- Toast System ---
    const addToast = useCallback((message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    }, []);

    // --- Loading timeout handler ---
    useEffect(() => {
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        if (mainLoading && !projects?.length) {
            loadingTimeoutRef.current = setTimeout(() => {
                console.warn('⚠️ Loading timeout - allowing navigation');
                setLoadingTimeout(true);
                addToast('Loading is taking longer than expected. You can navigate away.', 'Warning');
            }, LOADING_TIMEOUT);
        } else {
            setLoadingTimeout(false);
        }

        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [mainLoading, projects, addToast]);

    // --- Navigation guard ---
    useEffect(() => {
        console.log('🔍 DevelopmentPage mounted');
        console.log('📊 Loading state:', mainLoading);
        console.log('📊 Projects count:', projects?.length || 0);

        return () => {
            console.log('🚪 DevelopmentPage unmounting');
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [mainLoading, projects]);

    const activeClients = useMemo(() => {
        if (!clients) return [];
        return clients.filter(client => client.situacao === 'Ativo');
    }, [clients]);

    const clientOptions = useMemo(() => {
        return activeClients.map(client => ({
            id: client.id,
            codigo: client.codigo,
            razaoSocial: client.razaoSocial,
            nomeFantasia: client.nomeFantasia,
            cnpjCpf: client.cnpjCpf,
            contato: client.contato || '',
            displayName: client.nomeFantasia || client.razaoSocial
        }));
    }, [activeClients]);

    // ============================================
    // 3.1 NOTIFICATION FUNCTIONS
    // ============================================

    const notifyDevelopmentTeam = useCallback(async (projectData, projectId) => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];
                const permissions = Array.isArray(userData.permissions) ? userData.permissions : [];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Gerente de Projetos') ||
                    permissions.includes('desenvolvimento')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            console.log(`📢 Enviando notificações de projeto para ${targetUsers.length} usuários`);

            const config = getModuleConfig('desenvolvimento');
            const isEditing = !!editingItem;

            const baseNotification = {
                type: isEditing ? 'project_updated' : 'project_created',
                module: 'desenvolvimento',
                moduleIcon: config?.icon || '🧪',
                moduleColor: config?.color || '#6f42c1',
                moduleName: config?.name || 'Desenvolvimento de Projetos',
                title: isEditing ? '🔄 Projeto Atualizado' : '🧪 Novo Projeto de Desenvolvimento',
                fullMessage: `Detalhes do projeto:\n• Código: ${projectData.code}\n• Projeto: ${projectData.projectName}\n• Cliente: ${projectData.clientName || 'N/A'}\n• Situação: ${projectData.situation}\n• Tipo: ${projectData.briefing?.productType || 'N/A'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: 'Média',
                route: `/desenvolvimento`,
                data: {
                    projectId: projectId,
                    projectCode: projectData.code,
                    projectName: projectData.projectName,
                    clientName: projectData.clientName,
                    situation: projectData.situation
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                addNotification({
                    ...baseNotification,
                    id: `dev_${isEditing ? 'updated' : 'created'}_${projectId}_${user.uid}_${Date.now()}`,
                    message: isEditing ?
                        `${user.nome || 'Usuário'}, o projeto ${projectData.code} - ${projectData.projectName} foi atualizado por ${currentUser?.nome || 'Alguém'}` :
                        `${user.nome || 'Usuário'}, um novo projeto de desenvolvimento foi criado: ${projectData.code} - ${projectData.projectName} para ${projectData.clientName || 'cliente'}`,
                    userId: user.uid
                });

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...baseNotification,
                        id: `dev_${isEditing ? 'updated' : 'created'}_${projectId}_${user.uid}_${Date.now()}`,
                        message: isEditing ?
                            `${user.nome || 'Usuário'}, o projeto ${projectData.code} - ${projectData.projectName} foi atualizado por ${currentUser?.nome || 'Alguém'}` :
                            `${user.nome || 'Usuário'}, um novo projeto de desenvolvimento foi criado: ${projectData.code} - ${projectData.projectName} para ${projectData.clientName || 'cliente'}`,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao enviar notificações:', error);
        }
    }, [currentUser, addNotification, editingItem]);

    // ============================================
    // 3.2 AUXILIARY FUNCTIONS
    // ============================================

    const generateNextCode = useCallback(() => {
        const prefix = 'DEV';
        const currentYear = new Date().getFullYear();

        if (!projects || projects.length === 0) {
            return `${prefix}001.${currentYear}`;
        }

        const currentYearProjects = projects.filter(p => {
            if (!p.code) return false;
            return p.code.endsWith(`.${currentYear}`);
        });

        if (currentYearProjects.length === 0) {
            return `${prefix}001.${currentYear}`;
        }

        const numbers = currentYearProjects
            .map(p => {
                const match = p.code.match(/^DEV(\d+)\.\d{4}$/);
                return match ? parseInt(match[1], 10) : null;
            })
            .filter(num => num !== null && !isNaN(num));

        let nextNumber = 1;
        if (numbers.length > 0) {
            nextNumber = Math.max(...numbers) + 1;
        }
        return `${prefix}${nextNumber.toString().padStart(3, '0')}.${currentYear}`;
    }, [projects]);

    const isCodeDuplicate = useCallback((codeToCheck, excludeId = null) => {
        if (!projects) return false;
        return projects.some(p => p.code === codeToCheck && p.id !== excludeId);
    }, [projects]);

    const resetForm = useCallback(() => {
        setEditingItem(null);
        setCode('');
        setProjectName('');
        setClientId('');
        setClientName('');
        setClientContact('');
        setDescription('');
        setSituation(DEFAULT_SITUATION);
        setBriefing({
            ...DEFAULT_PROJECT_MODEL.briefing,
            startDate: new Date().toISOString().split('T')[0]
        });
        setFormError('');
        setFormSuccess('');
        setFunctionInput('');
        setAdditionalInfoInput('');
        setOtherKeyClaimsInput('');
    }, []);

    const closeModal = useCallback(() => {
        resetForm();
        setIsModalOpen(false);
        setIsSubmitting(false);
    }, [resetForm]);

    // --- Briefing Functions ---
    const handleAddProductFunction = useCallback(() => {
        if (functionInput.trim() && !briefing.productFunctions.includes(functionInput.trim())) {
            setBriefing(prev => ({
                ...prev,
                productFunctions: [...prev.productFunctions, functionInput.trim()]
            }));
            setFunctionInput('');
        }
    }, [functionInput, briefing.productFunctions]);

    const handleRemoveProductFunction = useCallback((func) => {
        setBriefing(prev => ({
            ...prev,
            productFunctions: prev.productFunctions.filter(f => f !== func)
        }));
    }, []);

    const handleAddAdditionalInfo = useCallback(() => {
        if (additionalInfoInput.trim() && !briefing.additionalInfo.includes(additionalInfoInput.trim())) {
            setBriefing(prev => ({
                ...prev,
                additionalInfo: [...prev.additionalInfo, additionalInfoInput.trim()]
            }));
            setAdditionalInfoInput('');
        }
    }, [additionalInfoInput, briefing.additionalInfo]);

    const handleRemoveAdditionalInfo = useCallback((info) => {
        setBriefing(prev => ({
            ...prev,
            additionalInfo: prev.additionalInfo.filter(i => i !== info)
        }));
    }, []);

    const handleKeyClaimToggle = useCallback((claim) => {
        if (claim === 'Other') {
            setBriefing(prev => {
                const hasOther = prev.keyClaims.includes('Other');
                if (!hasOther && otherKeyClaimsInput) {
                    return {
                        ...prev,
                        keyClaims: [...prev.keyClaims, 'Other'],
                        otherKeyClaims: otherKeyClaimsInput
                    };
                } else if (hasOther) {
                    return {
                        ...prev,
                        keyClaims: prev.keyClaims.filter(c => c !== 'Other'),
                        otherKeyClaims: ''
                    };
                }
                return prev;
            });
        } else {
            setBriefing(prev => {
                const newClaims = prev.keyClaims.includes(claim)
                    ? prev.keyClaims.filter(c => c !== claim)
                    : [...prev.keyClaims, claim];
                return { ...prev, keyClaims: newClaims };
            });
        }
    }, [otherKeyClaimsInput]);

    const handleClientChange = useCallback((selectedClientId) => {
        const selectedClient = clientOptions.find(c => c.id === selectedClientId);
        if (selectedClient) {
            setClientId(selectedClient.id);
            setClientName(selectedClient.razaoSocial);
            if (selectedClient.contato) {
                setClientContact(selectedClient.contato);
            }
        } else {
            setClientId('');
            setClientName('');
        }
    }, [clientOptions]);

    // --- Save Project ---
    const handleSave = useCallback(async (e) => {
        e.preventDefault();

        if (!editingItem && !can.create) {
            setFormError(t('pages.development.noPermissionCreate'));
            addToast(t('pages.development.noPermissionCreate'), 'Error');
            return;
        }
        if (editingItem && !can.edit) {
            setFormError(t('pages.development.noPermissionEdit'));
            addToast(t('pages.development.noPermissionEdit'), 'Error');
            return;
        }

        setFormError('');
        setFormSuccess('');
        setIsSubmitting(true);

        if (!projectName.trim()) {
            setFormError(t('pages.development.projectNameRequired'));
            setIsSubmitting(false);
            return;
        }

        if (!clientId && !clientName.trim()) {
            setFormError(t('pages.development.clientRequired'));
            setIsSubmitting(false);
            return;
        }

        if (!briefing.productType.trim()) {
            setFormError(t('pages.development.productTypeRequired'));
            setIsSubmitting(false);
            return;
        }

        if (briefing.productType === 'Others' && !briefing.otherProductType.trim()) {
            setFormError(t('pages.development.specifyProductType'));
            setIsSubmitting(false);
            return;
        }

        if (!code.trim()) {
            setFormError(t('pages.development.codeRequired'));
            setIsSubmitting(false);
            return;
        }

        if (!editingItem && isCodeDuplicate(code.trim())) {
            setFormError(t('pages.development.codeExists', { code: code.trim() }));
            setIsSubmitting(false);
            return;
        }

        if (editingItem && isCodeDuplicate(code.trim(), editingItem.id)) {
            setFormError(t('pages.development.codeExists', { code: code.trim() }));
            setIsSubmitting(false);
            return;
        }

        const projectData = {
            code: code.trim().toUpperCase(),
            projectName: projectName.trim(),
            clientId: clientId,
            clientName: clientName.trim(),
            clientContact: clientContact.trim(),
            description: description.trim(),
            situation: situation,
            briefing: briefing,
            updatedAt: new Date().toISOString(),
        };

        if (!editingItem) {
            projectData.createdAt = new Date().toISOString();
            projectData.createdBy = currentUser?.nome || 'Usuário';
        }

        try {
            let result;
            let projectId;

            if (editingItem) {
                result = await updateDocument(editingItem.id, projectData);
                if (result.success) {
                    setFormSuccess(t('pages.development.updateSuccess'));
                    addToast(t('pages.development.updateSuccess'), 'Success');
                    await notifyDevelopmentTeam(projectData, editingItem.id);
                    await logActivity(
                        'project_updated',
                        `development-project/${editingItem.id}`,
                        `Projeto ${projectData.code} - ${projectData.projectName} atualizado`
                    );
                    setTimeout(closeModal, 1500);
                } else {
                    setFormError(`${t('pages.development.updateError')}: ${result.error}`);
                    addToast(`${t('pages.development.updateError')}: ${result.error}`, 'Error');
                    setIsSubmitting(false);
                }
            } else {
                result = await addDocument(projectData);
                if (result.success) {
                    projectId = result.id;
                    setFormSuccess(t('pages.development.createSuccess'));
                    addToast(t('pages.development.createSuccess'), 'Success');
                    await notifyDevelopmentTeam(projectData, projectId);
                    await logActivity(
                        'project_created',
                        `development-project/${projectId}`,
                        `Projeto ${projectData.code} - ${projectData.projectName} criado para ${projectData.clientName}`
                    );
                    setTimeout(closeModal, 1500);
                } else {
                    setFormError(`${t('pages.development.createError')}: ${result.error}`);
                    addToast(`${t('pages.development.createError')}: ${result.error}`, 'Error');
                    setIsSubmitting(false);
                }
            }
        } catch (error) {
            setFormError(t('pages.development.createError'));
            addToast(t('pages.development.createError'), 'Error');
            setIsSubmitting(false);
        }
    }, [
        editingItem, can.create, can.edit, t, addToast,
        projectName, clientId, clientName, clientContact,
        description, situation, briefing, code,
        isCodeDuplicate, updateDocument, addDocument,
        notifyDevelopmentTeam, logActivity, closeModal, currentUser
    ]);

    // --- CRUD Operations ---
    const handleNewClick = useCallback(() => {
        if (!can.create) {
            addToast(t('pages.development.noPermissionCreate'), 'Error');
            return;
        }
        resetForm();
        setCode(generateNextCode());
        setIsModalOpen(true);
    }, [can.create, addToast, t, resetForm, generateNextCode]);

    const handleEditClick = useCallback((item) => {
        if (!can.edit) {
            addToast(t('pages.development.noPermissionEdit'), 'Error');
            return;
        }
        setEditingItem(item);
        setCode(item.code || '');
        setProjectName(item.projectName || '');
        setClientId(item.clientId || '');
        setClientName(item.clientName || '');
        setClientContact(item.clientContact || '');
        setDescription(item.description || '');
        setSituation(item.situation || DEFAULT_SITUATION);
        setBriefing(item.briefing || DEFAULT_PROJECT_MODEL.briefing);
        setIsModalOpen(true);
    }, [can.edit, addToast, t]);

    const handleDeleteClick = useCallback(async (item) => {
        if (!can.delete) {
            addToast(t('pages.development.noPermissionDelete'), 'Error');
            return;
        }

        const confirmed = window.confirm(t('pages.development.deleteConfirm', { name: item.projectName }));
        if (!confirmed) return;

        setIsSubmitting(true);
        try {
            const result = await deleteDocument(item.id);
            if (result.success) {
                addToast(t('pages.development.deleteSuccess'), 'Success');
                await logActivity(
                    'project_deleted',
                    `development-project/${item.id}`,
                    `Projeto ${item.code} - ${item.projectName} removido`
                );
            } else {
                addToast(`${t('pages.development.deleteError')}: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast(t('pages.development.deleteError'), 'Error');
        } finally {
            setIsSubmitting(false);
        }
    }, [can.delete, addToast, t, deleteDocument, logActivity]);

    const handleExportBriefing = useCallback((project) => {
        if (!can.view) {
            addToast(t('pages.development.noPermissionExport'), 'Error');
            return;
        }
        exportBriefing({
            project: project,
            showNotification: addToast
        });
    }, [can.view, addToast, t]);

    // --- Filters and Pagination ---
    const filteredProjects = useMemo(() => {
        if (!projects) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return projects.filter(item => {
            const matchesSearch = (
                (item.projectName && item.projectName.toLowerCase().includes(lowerSearch)) ||
                (item.code && item.code.toLowerCase().includes(lowerSearch)) ||
                (item.clientName && item.clientName.toLowerCase().includes(lowerSearch))
            );
            const matchesSituation = !filterSituation || item.situation === filterSituation;
            return matchesSearch && matchesSituation;
        }).sort((a, b) => {
            const codeA = a.code || '';
            const codeB = b.code || '';
            return codeA.localeCompare(codeB, undefined, { numeric: true });
        });
    }, [projects, searchTerm, filterSituation]);

    const totalPages = useMemo(() => Math.ceil(filteredProjects.length / ITEMS_PER_PAGE), [filteredProjects]);

    const currentProjects = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredProjects, currentPage]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            startTransition(() => {
                setCurrentPage(newPage);
            });
        }
    }, [totalPages]);

    const handleSearch = useCallback((value) => {
        startTransition(() => {
            setSearchTerm(value);
            setCurrentPage(1);
        });
    }, []);

    const handleFilterChange = useCallback((value) => {
        startTransition(() => {
            setFilterSituation(value);
            setCurrentPage(1);
        });
    }, []);

    // --- Navigate Home Handler ---
    const handleNavigateHome = useCallback(() => {
        navigate('/');
    }, [navigate]);

    // --- Modal Render ---
    const renderProjectModal = useCallback(() => {
        if (!isModalOpen) return null;

        return (
            <div className={styles.modalBackdrop} onClick={closeModal}>
                <div className={styles.formContainer} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>{editingItem ? t('pages.development.editProject') : t('pages.development.newProject')}</h2>
                            {editingItem && (
                                <p className={styles.modalSubtitle}>
                                    {t('pages.development.editing', { name: editingItem.projectName, code: editingItem.code })}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={closeModal}
                            className={styles.modalCloseButton}
                            disabled={isSubmitting}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formError && (
                        <div className={`${styles.alert} ${styles.error}`}>
                            <span className={styles.alertIcon}><FiAlertCircle size={18} /></span>
                            {formError}
                        </div>
                    )}

                    {formSuccess && (
                        <div className={`${styles.alert} ${styles.success}`}>
                            <span className={styles.alertIcon}><FiCheck size={18} /></span>
                            {formSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSave} className={styles.formContent}>
                        {/* SECTION 1: BASIC INFORMATION */}
                        <FormSection title={t('pages.development.form.basicInformation')} icon={<FiPackage size={18} />} required>
                            <div className={styles.twoColumnGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.code')} <span className={styles.required}>*</span></label>
                                    <input
                                        type="text"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        className={styles.input}
                                        placeholder={t('pages.development.form.codePlaceholder')}
                                        disabled={isSubmitting || !can.create}
                                        required
                                    />
                                    {!editingItem && (
                                        <small className={styles.fieldHint}>{t('pages.development.form.codeHint')}</small>
                                    )}
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.projectName')} <span className={styles.required}>*</span></label>
                                    <input
                                        type="text"
                                        value={projectName}
                                        onChange={(e) => setProjectName(e.target.value)}
                                        className={styles.input}
                                        placeholder={t('pages.development.form.projectNamePlaceholder')}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                        required
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.client')} <span className={styles.required}>*</span></label>
                                    <select
                                        value={clientId}
                                        onChange={(e) => handleClientChange(e.target.value)}
                                        className={styles.select}
                                        disabled={isSubmitting || clientsLoading || (!can.create && !can.edit)}
                                        required
                                    >
                                        <option value="">{t('pages.development.form.clientPlaceholder')}</option>
                                        {clientOptions.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.displayName} {client.codigo && `(${client.codigo})`}
                                            </option>
                                        ))}
                                    </select>
                                    {clientsLoading && (
                                        <small className={styles.fieldHint}>🔄 {t('common.loading')}</small>
                                    )}
                                    {!clientsLoading && clientOptions.length === 0 && (
                                        <small className={styles.fieldHint}>⚠️ No active clients found. Please register a client first.</small>
                                    )}
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.clientContact')}</label>
                                    <input
                                        type="text"
                                        value={clientContact}
                                        onChange={(e) => setClientContact(e.target.value)}
                                        className={styles.input}
                                        placeholder={t('pages.development.form.clientContactPlaceholder')}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    />
                                </div>
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.description')}</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        className={styles.textarea}
                                        rows={3}
                                        placeholder={t('pages.development.form.descriptionPlaceholder')}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    />
                                </div>
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.situation')}</label>
                                    <select
                                        value={situation}
                                        onChange={(e) => setSituation(e.target.value)}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    >
                                        <option value="Active">{t('pages.development.situation.active')}</option>
                                        <option value="On Hold">{t('pages.development.situation.onHold')}</option>
                                        <option value="Completed">{t('pages.development.situation.completed')}</option>
                                        <option value="Cancelled">{t('pages.development.situation.cancelled')}</option>
                                    </select>
                                </div>
                            </div>
                        </FormSection>

                        {/* SECTION 2: PROJECT BRIEFING */}
                        <FormSection title={t('pages.development.form.projectBriefing')} icon={<FiClipboard size={18} />}>
                            <div className={styles.twoColumnGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.startDate')}</label>
                                    <input
                                        type="date"
                                        value={briefing.startDate}
                                        className={styles.input}
                                        disabled
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <small className={styles.fieldHint}>{t('pages.development.form.startDateHint')}</small>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.brand')}</label>
                                    <input
                                        type="text"
                                        value={briefing.brandName}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, brandName: e.target.value }))}
                                        className={styles.input}
                                        placeholder={t('pages.development.form.brandPlaceholder')}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    />
                                </div>
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.targetAudience')}</label>
                                    <select
                                        value={briefing.targetAudience}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, targetAudience: e.target.value }))}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    >
                                        <option value="">{t('pages.development.form.targetAudiencePlaceholder')}</option>
                                        {TARGET_AUDIENCE_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <h4 className={styles.fieldGroupTitle} style={{ marginTop: '24px', marginBottom: '12px' }}>{t('pages.development.form.productCharacteristics')}</h4>
                            <div className={styles.twoColumnGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.productType')} <span className={styles.required}>*</span></label>
                                    <select
                                        value={briefing.productType}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, productType: e.target.value }))}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                        required
                                    >
                                        <option value="">{t('pages.development.form.productTypePlaceholder')}</option>
                                        {PRODUCT_TYPE_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                {briefing.productType === 'Others' && (
                                    <div className={styles.inputGroup}>
                                        <label>{t('pages.development.form.specifyType')} <span className={styles.required}>*</span></label>
                                        <input
                                            type="text"
                                            value={briefing.otherProductType}
                                            onChange={(e) => setBriefing(prev => ({ ...prev, otherProductType: e.target.value }))}
                                            className={styles.input}
                                            placeholder={t('pages.development.form.specifyTypePlaceholder')}
                                            disabled={isSubmitting || (!can.create && !can.edit)}
                                        />
                                    </div>
                                )}
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.physicalForm')}</label>
                                    <select
                                        value={briefing.physicalForm}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, physicalForm: e.target.value }))}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    >
                                        <option value="">{t('pages.development.form.physicalFormPlaceholder')}</option>
                                        {PHYSICAL_FORM_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.viscosity')}</label>
                                    <select
                                        value={briefing.viscosity}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, viscosity: e.target.value }))}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    >
                                        <option value="">{t('pages.development.form.viscosityPlaceholder')}</option>
                                        {VISCOSITY_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.fragrance')}</label>
                                    <select
                                        value={briefing.fragrance}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, fragrance: e.target.value }))}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    >
                                        <option value="">{t('pages.development.form.fragrancePlaceholder')}</option>
                                        {FRAGRANCE_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                {briefing.fragrance === 'Other' && (
                                    <div className={styles.inputGroup}>
                                        <label>{t('pages.development.form.specifyFragrance')}</label>
                                        <input
                                            type="text"
                                            value={briefing.otherFragrance}
                                            onChange={(e) => setBriefing(prev => ({ ...prev, otherFragrance: e.target.value }))}
                                            className={styles.input}
                                            placeholder={t('pages.development.form.specifyFragrancePlaceholder')}
                                            disabled={isSubmitting || (!can.create && !can.edit)}
                                        />
                                    </div>
                                )}
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.regulatoryCompliance')}</label>
                                    <select
                                        value={briefing.requiredCompliance}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, requiredCompliance: e.target.value }))}
                                        className={styles.select}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    >
                                        <option value="">{t('pages.development.form.regulatoryCompliancePlaceholder')}</option>
                                        {COMPLIANCE_OPTIONS.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                {briefing.requiredCompliance === 'Others' && (
                                    <div className={styles.inputGroup}>
                                        <label>{t('pages.development.form.specifyCompliance')}</label>
                                        <input
                                            type="text"
                                            value={briefing.otherCompliance}
                                            onChange={(e) => setBriefing(prev => ({ ...prev, otherCompliance: e.target.value }))}
                                            className={styles.input}
                                            placeholder={t('pages.development.form.specifyCompliancePlaceholder')}
                                            disabled={isSubmitting || (!can.create && !can.edit)}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.competitors')}</label>
                                    <textarea
                                        value={briefing.competitors}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, competitors: e.target.value }))}
                                        className={styles.textarea}
                                        rows={3}
                                        placeholder={t('pages.development.form.competitorsPlaceholder')}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    />
                                </div>
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.keyClaims')}</label>
                                    <div className={styles.checkboxGrid}>
                                        {KEY_CLAIMS_OPTIONS.map(claim => (
                                            <label key={claim} className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={briefing.keyClaims.includes(claim)}
                                                    onChange={() => handleKeyClaimToggle(claim)}
                                                    disabled={isSubmitting || (!can.create && !can.edit)}
                                                />
                                                {claim}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {briefing.keyClaims.includes('Other') && (
                                    <div className={styles.inputGroup} style={{ marginTop: '12px' }}>
                                        <label>{t('pages.development.form.specifyClaims')}</label>
                                        <input
                                            type="text"
                                            value={briefing.otherKeyClaims}
                                            onChange={(e) => setBriefing(prev => ({ ...prev, otherKeyClaims: e.target.value }))}
                                            className={styles.input}
                                            placeholder={t('pages.development.form.specifyClaimsPlaceholder')}
                                            disabled={isSubmitting || (!can.create && !can.edit)}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.productFunctions')}</label>
                                    <div className={styles.listInputContainer}>
                                        <div className={styles.listInputRow}>
                                            <input
                                                type="text"
                                                value={functionInput}
                                                onChange={(e) => setFunctionInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleAddProductFunction()}
                                                className={styles.input}
                                                placeholder={t('pages.development.form.productFunctionsPlaceholder')}
                                                disabled={isSubmitting || (!can.create && !can.edit)}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddProductFunction}
                                                className={styles.addButtonSmall}
                                                disabled={isSubmitting || (!can.create && !can.edit)}
                                            >
                                                <FiPlus size={18} />
                                            </button>
                                        </div>
                                        <div className={styles.listItems}>
                                            {briefing.productFunctions.map((func, index) => (
                                                <span key={index} className={styles.listItem}>
                                                    {func}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveProductFunction(func)}
                                                        className={styles.removeItemButton}
                                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <h4 className={styles.fieldGroupTitle} style={{ marginTop: '24px', marginBottom: '12px' }}>{t('pages.development.form.timeline')}</h4>
                            <div className={styles.twoColumnGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.sampleDelivery')}</label>
                                    <input
                                        type="date"
                                        value={briefing.sampleDeliveryDate}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, sampleDeliveryDate: e.target.value }))}
                                        className={styles.input}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.finalProductDelivery')}</label>
                                    <input
                                        type="date"
                                        value={briefing.finalProductDeliveryDate}
                                        onChange={(e) => setBriefing(prev => ({ ...prev, finalProductDeliveryDate: e.target.value }))}
                                        className={styles.input}
                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                    />
                                </div>
                            </div>

                            <div className={styles.fullWidth} style={{ marginTop: '16px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.development.form.additionalInfo')}</label>
                                    <div className={styles.listInputContainer}>
                                        <div className={styles.listInputRow}>
                                            <input
                                                type="text"
                                                value={additionalInfoInput}
                                                onChange={(e) => setAdditionalInfoInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleAddAdditionalInfo()}
                                                className={styles.input}
                                                placeholder={t('pages.development.form.additionalInfoPlaceholder')}
                                                disabled={isSubmitting || (!can.create && !can.edit)}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddAdditionalInfo}
                                                className={styles.addButtonSmall}
                                                disabled={isSubmitting || (!can.create && !can.edit)}
                                            >
                                                <FiPlus size={18} />
                                            </button>
                                        </div>
                                        <div className={styles.listItems}>
                                            {briefing.additionalInfo.map((info, index) => (
                                                <span key={index} className={styles.listItem}>
                                                    {info}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAdditionalInfo(info)}
                                                        className={styles.removeItemButton}
                                                        disabled={isSubmitting || (!can.create && !can.edit)}
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </FormSection>

                        <div className={styles.formActions}>
                            <button
                                type="button"
                                onClick={closeModal}
                                className={styles.secondaryButton}
                                disabled={isSubmitting}
                            >
                                <FiX size={18} /> {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                className={styles.primaryButton}
                                disabled={isSubmitting || (!can.create && !can.edit)}
                            >
                                {isSubmitting ? (
                                    <>
                                        <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                        {t('common.saving')}
                                    </>
                                ) : (
                                    editingItem ? <><FiSave size={18} /> {t('common.saveChanges')}</> : <><FiPlus size={18} /> {t('pages.development.newProject')}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }, [
        isModalOpen, closeModal, editingItem, t, isSubmitting,
        formError, formSuccess, handleSave, code, projectName,
        clientId, clientOptions, clientsLoading, clientContact,
        description, situation, briefing, functionInput,
        additionalInfoInput, handleClientChange, can.create,
        can.edit, handleKeyClaimToggle, handleAddProductFunction,
        handleRemoveProductFunction, handleAddAdditionalInfo,
        handleRemoveAdditionalInfo
    ]);

    // --- Main Render ---
    const showSkeleton = mainLoading && !projects?.length;

    if (loadingTimeout) {
        return <LoadingOverlay onNavigateHome={handleNavigateHome} />;
    }

    if (showSkeleton) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}>
                        <div className={styles.spinner}></div>
                        <p>{t('pages.development.loadingProjects')}</p>
                        <button
                            onClick={handleNavigateHome}
                            className={styles.navigationButton}
                        >
                            <FiHome size={18} />
                            {t('common.goToDashboard')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            {/* Toast System */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Form Modal */}
            {renderProjectModal()}

            {/* Header */}
            <PageHeader
                title={t('pages.development.title')}
                actionText={t('pages.development.newProject')}
                onAction={handleNewClick}
                disabled={mainLoading || !can.create}
            />

            {/* Search and Filters Bar */}
            <div className={styles.searchBar}>
                <div className={styles.filterRow}>
                    <div className={styles.inputGroup} style={{ flex: 2 }}>
                        <label>{t('common.search')}</label>
                        <SearchBar
                            placeholder={t('pages.development.search.placeholder')}
                            onSearch={handleSearch}
                            disabled={showSkeleton || isPending}
                            initialValue={searchTerm}
                            debounceDelay={300}
                        />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: 1 }}>
                        <label>{t('pages.development.filters.situation')}</label>
                        <select
                            value={filterSituation}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className={styles.select}
                            disabled={showSkeleton || isPending}
                        >
                            <option value="">{t('pages.development.filters.all')}</option>
                            <option value="Active">{t('pages.development.situation.active')}</option>
                            <option value="On Hold">{t('pages.development.situation.onHold')}</option>
                            <option value="Completed">{t('pages.development.situation.completed')}</option>
                            <option value="Cancelled">{t('pages.development.situation.cancelled')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Projects Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '100px' }}>{t('pages.development.table.code')}</th>
                            <th>{t('pages.development.table.projectName')}</th>
                            <th>{t('pages.development.table.client')}</th>
                            <th style={{ width: '110px' }}>{t('pages.development.table.startDate')}</th>
                            <th style={{ width: '120px' }}>{t('pages.development.table.situation')}</th>
                            <th style={{ width: '180px' }}>{t('pages.development.table.projectStatus')}</th>
                            <th style={{ width: '200px' }}>{t('pages.development.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentProjects.length === 0 ? (
                            <tr>
                                <td colSpan="7" className={styles.emptyTable}>
                                    {searchTerm || filterSituation
                                        ? t('pages.development.table.emptyFiltered')
                                        : t('pages.development.table.empty')}
                                </td>
                            </tr>
                        ) : (
                            currentProjects.map(project => (
                                <tr key={project.id} className={styles.tableRow}>
                                    <td><strong>{project.code}</strong></td>
                                    <td>{project.projectName}</td>
                                    <td>{project.clientName}</td>
                                    <td>
                                        {project.briefing?.startDate
                                            ? new Date(project.briefing.startDate).toLocaleDateString('pt-BR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            })
                                            : '-'
                                        }
                                    </td>
                                    <td><StatusBadge status={project.situation} t={t} /></td>
                                    <td><ProjectStepStatus project={project} t={t} /></td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            {can.edit && (
                                                <button
                                                    onClick={() => handleEditClick(project)}
                                                    className={`${styles.actionButton} ${styles.editButton}`}
                                                    disabled={isSubmitting || isPending}
                                                    title={t('common.edit')}
                                                    type="button"
                                                >
                                                    <FiEdit size={16} />
                                                </button>
                                            )}
                                            {can.generate && (
                                                <button
                                                    onClick={() => handleExportBriefing(project)}
                                                    className={`${styles.actionButton} ${styles.briefingButton}`}
                                                    disabled={isSubmitting || isPending}
                                                    title={t('pages.development.actions.exportBriefing')}
                                                    type="button"
                                                >
                                                    <GrDocumentTransfer />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDeleteClick(project)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    disabled={isSubmitting || isPending}
                                                    title={t('common.delete')}
                                                    type="button"
                                                >
                                                    <FiTrash2 size={16} />
                                                </button>
                                            )}
                                            {!can.view && !can.edit && !can.generate && !can.delete && (
                                                <span className={styles.textMuted}>{t('pages.development.noActions')}</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && !showSkeleton && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredProjects.length}
                    onPageChange={handlePageChange}
                    disabled={isSubmitting || isPending}
                />
            )}
        </div>
    );
};

export default DevelopmentPage;